import React from "react";
import { motion } from "framer-motion";
import { Flame, Calendar } from "lucide-react";

/**
 * StreakCard — shows weekly streak, this-week count, motivational message,
 * and a 12-week consistency heat map (GitHub-style).
 *
 * Props:
 *   engagement: {
 *     weekly_streak, this_week_count, last_week_count, message, heat_map,
 *     total_workouts, days_since_last
 *   }
 */
export const StreakCard = ({ engagement }) => {
  if (!engagement) return null;

  const {
    weekly_streak = 0,
    this_week_count = 0,
    message = "",
    heat_map = [],
    total_workouts = 0,
  } = engagement;

  const streakLabel = weekly_streak === 1 ? "week streak" : "weeks streak";

  // Heat map cell color based on workout count that day
  const cellClass = (count) => {
    if (count === 0) return "bg-[#1E293B]/60";
    if (count === 1) return "bg-success/40";
    if (count === 2) return "bg-success/70";
    return "bg-success";
  };

  const dayLabels = ["M", "T", "W", "T", "F", "S", "S"];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-surface border border-border rounded-xl p-4 sm:p-6"
      data-testid="streak-card"
    >
      {/* Top row: lifetime identity + streak + this-week */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="min-w-0 flex-1">
          {/* Lifetime workouts — the un-loseable identity stat (Hormozi pattern) */}
          <div className="flex items-baseline gap-2 mb-1.5">
            <span
              className="font-heading text-3xl sm:text-4xl font-black text-white"
              data-testid="lifetime-workouts"
            >
              {total_workouts}
            </span>
            <span className="text-text-secondary text-sm">
              lifetime workout{total_workouts === 1 ? "" : "s"}
            </span>
          </div>
          {weekly_streak > 0 && (
            <div className="flex items-center gap-1.5 mb-2 text-xs text-amber-400/90">
              <Flame className="w-3.5 h-3.5 flex-shrink-0" />
              <span data-testid="streak-count">
                {weekly_streak} {streakLabel}
              </span>
            </div>
          )}
          <p
            className="text-text-secondary text-sm sm:text-base leading-relaxed"
            data-testid="streak-message"
          >
            {message}
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-xs text-text-muted uppercase tracking-wide">
            This week
          </p>
          <p className="font-heading text-3xl font-bold text-success" data-testid="this-week-count">
            {this_week_count}
          </p>
        </div>
      </div>

      {/* Heat map */}
      {heat_map.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs uppercase tracking-wide text-text-muted flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              Last 12 weeks
            </p>
          </div>

          <div className="flex gap-2">
            {/* Day labels column */}
            <div className="flex flex-col gap-1 pt-0.5">
              {dayLabels.map((d, i) => (
                <div
                  key={i}
                  className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[9px] text-text-muted flex items-center"
                >
                  {i % 2 === 0 ? d : ""}
                </div>
              ))}
            </div>

            {/* Heat map grid */}
            <div className="flex gap-1 overflow-x-auto scrollbar-hide flex-1">
              {heat_map.map((week, wi) => (
                <div
                  key={wi}
                  className="flex flex-col gap-1 flex-shrink-0"
                  data-testid={`heatmap-week-${wi}`}
                >
                  {week.days.map((day, di) => (
                    <div
                      key={di}
                      title={`${day.date}: ${day.count} workout${day.count === 1 ? "" : "s"}`}
                      className={`w-3 h-3 sm:w-3.5 sm:h-3.5 rounded-sm ${cellClass(day.count)}`}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center justify-end gap-1.5 mt-3 text-[10px] text-text-muted">
            <span>Less</span>
            <div className="w-3 h-3 rounded-sm bg-[#1E293B]/60" />
            <div className="w-3 h-3 rounded-sm bg-success/40" />
            <div className="w-3 h-3 rounded-sm bg-success/70" />
            <div className="w-3 h-3 rounded-sm bg-success" />
            <span>More</span>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default StreakCard;
